/**
 * P5 offline save queue — PURE logic (no I/O).
 *
 * A run save that fails in a dead zone or on a server blip is never lost:
 * it's queued locally (Capacitor Preferences — see saveQueueStore.ts) and
 * retried automatically. Each queued run carries a client-minted
 * idempotency key (`clientKey`) so a retry can't double-save — the server's
 * partial-unique (user_id, client_key) makes the second write return the
 * already-saved row.
 *
 * This module is the vitest-tested core: classify a failure, enqueue,
 * record an attempt, remove, re-arm. The Preferences adapter + drain driver
 * (the plugin boundary) live in saveQueueStore.ts.
 *
 * ── B56 — WHOSE RUN IS THIS? (2026-08-29) ─────────────────────────────────
 * The store (`tayro.runSaveQueue.v1`) is a DEVICE-GLOBAL Capacitor Preferences
 * blob — native UserDefaults, which survives app kill by design and is not
 * scoped to an account. Until this change nothing in a queued run said who
 * recorded it, and `drainQueue(fetchAuthed)` flushed the whole queue under
 * whatever JWT happened to be current. On a shared device (a family iPhone, a
 * team's loaner) a run user A recorded in a dead zone was POSTed into user B's
 * account on B's next foreground/online/app-open drain: A's real GPS trace,
 * written into B's history under B's user_id.
 *
 * Found by the B46 audit (`.claude/briefs/B46-LANE-REPORT.md`, leak A) — the
 * same "unstamped device blob adopted by the next session" mechanism as the
 * "Week 9 is in." chip, but moving GPS data rather than a label.
 *
 * The rule, mirroring `lib/stampedSurfaceState.ts`:
 *
 *   - stamped with THIS viewer → the ordinary case: drains, renders, mutates.
 *   - stamped with ANOTHER id  → FOREIGN. Never POSTed, never rendered, and
 *                                never mutated or deleted by this session —
 *                                it is somebody's real run and it drains when
 *                                its owner signs back in. Retained.
 *   - UNSTAMPED (legacy)       → UNATTRIBUTED. Never POSTed, never rendered,
 *                                never deleted by the ownership rule. See the
 *                                legacy note on `ownershipOf` below.
 *   - no viewer signed in      → nothing is anyone's: nothing drains.
 *
 * Where B46 SCRUBS foreign state, this module RETAINS it. That divergence is
 * the whole point: the chip was a five-second ✓ for a save that had already
 * completed server-side, so dropping it cost nothing. A queued run is the only
 * copy of a run that has not reached any server. Deleting it is data loss, so
 * the only safe verb for another account's entry is *leave it alone*.
 *
 * Retention is bounded — see `MAX_RETAINED_FOREIGN`.
 */

import type { RouteCoord } from './geo';

/** After this many auto-retries a transient failure stops auto-retrying and
 *  waits for the athlete (manual retry). Never silent-drop. */
export const MAX_AUTO_ATTEMPTS = 5;

/**
 * B56 — hard ceiling on entries the CURRENT actor can never act on (other
 * accounts' runs + unattributed legacy entries), so a device-global store that
 * now deliberately keeps other people's data cannot grow without bound.
 *
 * 10, and the arithmetic is the reason: an uploaded route is capped at 2000
 * 4-tuple vertices (`geo.ts` `routeForUpload`), i.e. roughly 90 KB of JSON per
 * worst-case entry, so this bounds foreign data at ~0.9 MB of UserDefaults —
 * generous for a store that is not meant to hold megabytes.
 *
 * It should never bite. Reaching it needs ten failed-and-never-drained saves
 * belonging to accounts other than the one enqueuing, on one device, when the
 * queue re-drains on every foreground and every `online` event. Eviction is
 * oldest-first (`queuedAt`) and happens only at enqueue — the one moment the
 * store grows — and never touches the enqueuing account's own runs.
 */
export const MAX_RETAINED_FOREIGN = 10;

/** The exact body POSTed to /api/runs — carries the idempotency key. */
export interface RunSavePayload {
  clientKey: string;
  startedAt: string;
  endedAt: string;
  durationS: number;
  distanceM: number;
  avgPaceSPerMi: number | null;
  elevationGainM: number;
  route: RouteCoord[] | null;
  source: string;
}

export type QueueState = 'pending' | 'needs_attention';

export interface QueuedRun {
  payload: RunSavePayload;
  queuedAt: number;
  attempts: number;
  /** HTTP status of the last drain attempt; null = network error. */
  lastStatus: number | null;
  lastError: string | null;
  state: QueueState;
  /**
   * B56 — the CLAIM: the auth user id of the account that recorded this run,
   * stamped at enqueue. Optional in the type on purpose: entries persisted by
   * a build that predates B56 come back off disk without it, and pretending
   * otherwise would just move the lie into the type. `undefined` (and `null`)
   * mean *unattributed* — see `ownershipOf`.
   */
  userId?: string | null;
}

/**
 * B56 — a queued run, seen from one viewer's chair.
 *   own          — this viewer recorded it. Drains, renders, is mutable.
 *   foreign      — another account recorded it. Retained, untouched.
 *   unattributed — nobody can be shown to have recorded it. Retained, inert.
 */
export type QueueOwnership = 'own' | 'foreign' | 'unattributed';

/**
 * Is a failed save worth auto-retrying?
 *   network error (null) or 5xx → yes (transient — the save may still land)
 *   4xx → no (the server rejected it: bad payload, or consent lost — a
 *         retry can't fix it; surface it honestly and wait for a person)
 */
export function isTransient(status: number | null): boolean {
  return status === null || status >= 500;
}

/** Mint a stable idempotency key for one run. crypto.randomUUID in every
 *  target (WKWebView secure context, localhost); the string fallback exists
 *  only for a non-secure web context — the server unique index is the real
 *  dedup guard regardless. */
export function newClientKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * B56 — classify one queued run for one viewer. The single place the ownership
 * question is answered; everything else in this module and the store defers to
 * it, so there is no ownership-agnostic path left to reach for.
 *
 * ── The legacy (unattributed) ruling, and why it is not B26's ──────────────
 * B26's rule for the onboarding wizard blob is "unclaimed is not foreign": an
 * unstamped blob is answers TYPED AT THIS BROWSER BY SOMEONE NOT YET SIGNED IN,
 * and the only person who can be sitting there signing in is the person who
 * typed them — so the first account to sign in CLAIMS them. That rescue does
 * not transfer here. A run only reaches this queue from a save that already
 * carried a JWT (a 5xx or a network error on an authenticated POST), so every
 * queued run had a signed-in author. "Unstamped" can therefore never mean
 * pre-auth work by the person now present; it can only mean an entry written
 * by a build older than this stamp — i.e. unattributable, i.e. exactly the leak.
 *
 * B46's rule for the chip is "unstamped → dropped", and half of that transfers.
 * We refuse to ACT on an unattributable entry for the same reason B46 refuses
 * to render one. We do not DELETE it, because the stakes are not the chip's:
 * a wrong POST writes someone's GPS trace into the wrong account (irreversible
 * from the client, and it pollutes both accounts' history), while withholding
 * costs only a sync that does not happen. Between an irreversible wrong write
 * and a reversible non-write, take the non-write.
 *
 * So an unattributed entry is QUARANTINED: never POSTed, never rendered, never
 * deleted by this rule. It sits in the retained budget (`MAX_RETAINED_FOREIGN`)
 * and ages out of it oldest-first if a device ever accumulates that many.
 *
 * The rejected alternative, on the record: claim-on-first-drain (adopt
 * unstamped entries for whoever drains first after the upgrade). It would
 * rescue the pre-upgrade athlete's run on a single-user device — and on a
 * shared one it would re-run B56 exactly once, at the worst possible moment,
 * with real GPS data. The population it would rescue is tiny (an entry must
 * have failed to save AND never drained before the upgrade, across a queue
 * that re-drains on every foreground and every `online` event); the population
 * it would corrupt is small too, but corruption is not symmetric with delay.
 * If the founder ever wants the rescue, it is this function's `unattributed`
 * branch and nothing else.
 */
export function ownershipOf(
  item: QueuedRun,
  viewerId: string | null | undefined,
): QueueOwnership {
  const owner = item.userId;
  if (typeof owner !== 'string' || owner === '') return 'unattributed';
  if (typeof viewerId !== 'string' || viewerId === '') return 'foreign';
  return owner === viewerId ? 'own' : 'foreign';
}

/** The entries this viewer recorded — the ONLY entries any surface may show.
 *  A signed-out viewer owns nothing. */
export function ownedByUser(queue: QueuedRun[], viewerId: string | null | undefined): QueuedRun[] {
  return queue.filter((q) => ownershipOf(q, viewerId) === 'own');
}

/** The entries this viewer can never act on: other accounts' runs plus
 *  unattributed legacy entries. What `MAX_RETAINED_FOREIGN` bounds. */
export function retainedForUser(queue: QueuedRun[], viewerId: string | null | undefined): QueuedRun[] {
  return queue.filter((q) => ownershipOf(q, viewerId) !== 'own');
}

/**
 * Bound the retained (non-own) share of a device-global store, oldest first.
 *
 * Called from `enqueue` — the only path that grows the queue. Never evicts the
 * actor's own runs: those are governed by the drain/`MAX_AUTO_ATTEMPTS` cycle
 * and by the athlete, not by a storage cap.
 */
export function capRetainedForeign(
  queue: QueuedRun[],
  actorId: string | null | undefined,
  max: number = MAX_RETAINED_FOREIGN,
): QueuedRun[] {
  const retained = retainedForUser(queue, actorId);
  const excess = retained.length - Math.max(0, max);
  if (excess <= 0) return queue;
  // Oldest first; `queuedAt` ties resolve by insertion order (stable sort).
  const doomed = new Set(
    [...retained].sort((a, b) => a.queuedAt - b.queuedAt).slice(0, excess),
  );
  return queue.filter((q) => !doomed.has(q));
}

/**
 * Add a failed save to the queue, STAMPED with the account that recorded it
 * (B56). Idempotent on clientKey WITHIN one owner — the same run is never
 * queued twice, while a key collision across accounts (practically impossible:
 * `newClientKey` is a UUID) could never silently swallow the second athlete's
 * run.
 *
 * `userId` is nullable only so the caller never has to prove non-null at the
 * call site. A null stamp is fail-closed, not permissive: it produces an entry
 * `ownershipOf` calls `unattributed`, which never POSTs to anyone. It can only
 * arise from a save attempted with no session — which the server would reject
 * anyway, so nothing that could have synced is lost.
 */
export function enqueue(
  queue: QueuedRun[],
  payload: RunSavePayload,
  queuedAt: number,
  userId: string | null | undefined,
): QueuedRun[] {
  const owner = typeof userId === 'string' && userId ? userId : null;
  if (queue.some((q) => q.payload.clientKey === payload.clientKey && (q.userId ?? null) === owner)) {
    return queue;
  }
  return capRetainedForeign(
    [
      ...queue,
      {
        payload,
        queuedAt,
        attempts: 0,
        lastStatus: null,
        lastError: null,
        state: 'pending',
        userId: owner,
      },
    ],
    owner,
  );
}

/** Drop a run from the queue (it saved, or the athlete discarded it).
 *  Owner-scoped: a session can only ever delete its OWN entry — another
 *  account's queued run is the only copy of that run (B56). */
export function removeByKey(
  queue: QueuedRun[],
  clientKey: string,
  viewerId: string | null | undefined,
): QueuedRun[] {
  return queue.filter(
    (q) => !(q.payload.clientKey === clientKey && ownershipOf(q, viewerId) === 'own'),
  );
}

/** Record the outcome of a drain attempt for one item. A 4xx goes straight to
 *  needs_attention (real rejection); a transient failure needs_attention only
 *  after exhausting auto-retries. */
export function recordFailure(
  queue: QueuedRun[],
  clientKey: string,
  viewerId: string | null | undefined,
  status: number | null,
  errorMsg: string | null,
): QueuedRun[] {
  return queue.map((q) => {
    if (q.payload.clientKey !== clientKey || ownershipOf(q, viewerId) !== 'own') return q;
    const attempts = q.attempts + 1;
    const exhausted = !isTransient(status) || attempts >= MAX_AUTO_ATTEMPTS;
    return {
      ...q,
      attempts,
      lastStatus: status,
      lastError: errorMsg,
      state: exhausted ? 'needs_attention' : 'pending',
    };
  });
}

/**
 * Items eligible for an automatic drain pass BY THIS VIEWER. `needs_attention`
 * items wait for a manual retry (rearm) — they never auto-retry.
 *
 * B56 — there is deliberately no viewer-agnostic `drainable()`. The old one was
 * the bug: a POST list assembled without asking whose runs were on it, handed
 * to a `fetchAuthed` bound to whoever happened to be signed in. Ownership is a
 * required argument so the question cannot be skipped by a future caller.
 */
export function drainableForUser(
  queue: QueuedRun[],
  viewerId: string | null | undefined,
): QueuedRun[] {
  return queue.filter((q) => q.state === 'pending' && ownershipOf(q, viewerId) === 'own');
}

/** A manual retry re-arms a needs_attention item for the next drain.
 *  Owner-scoped: a viewer can only re-arm their own run (B56). */
export function rearm(
  queue: QueuedRun[],
  clientKey: string,
  viewerId: string | null | undefined,
): QueuedRun[] {
  return queue.map((q) =>
    q.payload.clientKey === clientKey && ownershipOf(q, viewerId) === 'own'
      ? { ...q, state: 'pending', lastError: null }
      : q,
  );
}
